import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import numeral from 'numeral';

// eslint-disable-next-line
import packageJson from '@root/package.json';
import { Data, User } from 'types/shared';

// utils/dateFormat.ts
const packageName = packageJson.name;

const currencyFormat = (value: any, format = '0,0.00', currency = '₱', pad = true) => `${currency}${pad ? ' ' : ''}${numeral(value).format(format)}`;

const shortDateFormat = (date: moment.MomentInput) => moment(date).format('MM/DD/YYYY');

// eslint-disable-next-line @typescript-eslint/no-use-before-define
const serverDateFormat = (date: moment.MomentInput) => formatDate(date);

const calculateAgeInDays = (createdAt: any) => {
  const createdDate: any = new Date(createdAt);
  const currentDate: any = new Date();
  const ageInMilliseconds = currentDate - createdDate;
  const ageInDays = Math.floor(ageInMilliseconds / (1000 * 60 * 60 * 24));

  return ageInDays;
};

// Replace underscore with spaces and capitalize words
const humanize = (str: string) => {
  const removedUnderscore = str.split('_');
  const capitalizedWords = removedUnderscore.map(
    (word) => word.slice(0, 1).toUpperCase() + word.slice(1),
  );
  const joinedWords = capitalizedWords.join(' ');
  // Capitalize words that has slash symbol
  const removeSlashed = joinedWords.split('/');
  const mappedSlashed = removeSlashed.map((word) => word.slice(0, 1).toUpperCase() + word.slice(1));
  // Capitalize words that has dash symbol
  const joinedMappedWords = mappedSlashed.join('/');
  const removeDashed = joinedMappedWords.split('-');
  const mappedDashed = removeDashed.map((word) => word.slice(0, 1).toUpperCase() + word.slice(1));
  return mappedDashed.join('-');
};

const time24HourFormat = (date: moment.MomentInput) => moment(date).format('HH:mm');

const time12HourFormat = (
  date: moment.MomentInput,
  showDate = false,
  showYear = false,
) => {
  if (showDate) return moment(date).format('h:mm A');

  if (showYear) return moment(date).format('MMM. D, YYYY h:mm A');

  return moment(date).format('MMM. D h:mm A');
};

const dateFormat = (date: moment.MomentInput) => moment(date).format('YYYY-MM-DD');

const timestampToMonthDay = (date: moment.MomentInput) => moment(date).format('MMM. DD');

const timestampToDate = (date: moment.MomentInput, showYear = false) => {
  const parsedDate = moment(date);
  if (showYear) return parsedDate.format('MMM. D, YYYY');

  const now = moment();
  return now.isSame(date, 'year') ? parsedDate.format('MMM. D') : parsedDate.format('MMM. D, YYYY');
};

const timestampToDayOfWeek = (date: moment.MomentInput) => moment(date).format('dddd');

const timestampToDateTime = (date: moment.MomentInput) => moment(date).format('MMMM D, YYYY h:mm a');

const normalizeMobile = (mobile: string) => {
  let normalizedMobile = mobile;
  if (mobile.startsWith('0')) {
    normalizedMobile = normalizedMobile.replace('0', '');
  } else if (mobile.startsWith('63')) {
    normalizedMobile = normalizedMobile.replace('63', '');
  } else if (mobile.startsWith('+63')) {
    normalizedMobile = normalizedMobile.replace('+63', '');
  }
  return normalizedMobile;
};

const getCurrentVersion = () => localStorage.getItem(`${packageName}-version`);

const isLatestVersion = (currentVersion: string) => currentVersion === getCurrentVersion();

const setLatestVersion = (version: string) => localStorage.setItem(`${packageName}-version`, version);

const localDate = (date: any) => moment.utc(date).local();

// eg. ex***@gmail.com
const formatEmail = (email: string) => email?.replace(/(\w{3})[\w\\+.-]+@([\w.]+\w)/, '$1***@$2');

const getFormData = (data: any = {}, excludeList: string[] = []) => {
  const formData = new FormData();
  Object.keys(data).filter((k) => !excludeList.includes(k)).forEach((key) => {
    const value = data[key];
    if (Array.isArray(data[key])) {
      value.forEach((d: any, i: number) => {
        if (d && typeof d === 'object' && !(d instanceof File)) {
          Object.keys(d).forEach((k) => {
            formData.append(`${key}[${i}][${k}]`, d[k] || '');
          });
        } else {
          formData.append(`${key}[]`, d || '');
        }
      });
    } else if (value && typeof value === 'object' && !(value instanceof File)) {
      Object.keys(data[key]).forEach((d) => {
        formData.append(`${key}[${d}]`, data[key][d] || '');
      });
    } else {
      formData.append(key, value || '');
    }
  });

  return formData;
};

const getInitials = (firstName?: string, lastName?: string) => {
  let firstInitial = firstName ? firstName.charAt(0) : '';
  if (firstName) {
    // Split the firstName by spaces and get the initials of the first and second words
    const names = firstName.trim().split(' ');
    if (names.length > 1) {
      firstInitial = `${names[0].charAt(0)}${names[1].charAt(0)}`;
    }
  }
  const lastInitial = lastName ? lastName.charAt(0) : '';

  // Concatenate the initials and return them
  return `${firstInitial}${lastInitial}`.toUpperCase();
};

const formatPhoneNumber = (phoneNumber: any) => {
  if (!phoneNumber) return '';
  // Remove any non-digit characters
  const cleaned = (`${phoneNumber}`).replace(/\D/g, '');

  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }

  return phoneNumber;
};

const formatFieldName = (field: any) => {
  if (!field) return '';
  return field
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (char: any) => char.toUpperCase()); // Capitalize the first letter of each word
};

const formatBytes = (bytes: any, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [ 'Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB' ];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // ** same as Math.pow(k, i)
  return `${parseFloat((bytes / (k ** i)).toFixed(dm))} ${sizes[i]}`;
};

declare global {
  interface Window {
    opera: any;
  }
}

// detect if the device was mobile/tablet
const mobileAndTabletCheck = () => {
  let check = false;
  // eslint-disable-next-line func-names, no-useless-escape
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; }(navigator.userAgent || navigator.vendor || window.opera));
  return check;
};

const formatShortNumber = (number: any) => numeral(number).format('0.[0]a');

const trimWysiwygValue = (text: any) => {
  if (!text) return '';
  // Remove leading <br> tags at the start of the paragraph
  const trimmedStart = text.replace(/(<p>\s*(<br\s*\/?>\s*)+)/, '<p>');

  // Remove trailing <br> tags at the end
  const newValue = trimmedStart.replace(/(<br\s*\/?>)+\s*<\/p>$/, '</p>');
  return newValue;
};

const hex = (c: string) => {
  const v = `0${c.charCodeAt(0).toString(16)}`;
  return `\\x${v.substr(v.length - 2)}`;
};

const formatDecimal = (number: any, decimal: any) => number.toFixed(decimal);

const stringEscape = (s: string) => (s ? s
  .replace(/\\/g, '\\\\')
  .replace(/\n/g, '\\n')
  .replace(/\t/g, '\\t')
  .replace(/\v/g, '\\v')
  .replace(/'/g, "\\'")
  .replace(/"/g, '\\"')
  // eslint-disable-next-line
  .replace(/[\x00-\x1F\x80-\x9F]/g, hex) : s);

// for dentist pov sent referrals
// wherein created or referred_to is equal to user/dentist id
const isDentistReferralSent = (user: any, data: any) => {
  const isTrue = user?.type === 'dentist'
    && (user?.id === data?.created_by
      || user?.id === data?.referred_by?.id)
    && user?.id !== data?.referred_to?.id;
  return isTrue;
};

// for dentist pov received referrals
// wherein referred_to is equal to user/dentist id
const isDentistReferralReceived = (user: any, data: any) => {
  const isTrue = user?.type === 'dentist'
    && user?.id === data?.referred_to?.id
    && user?.id !== data?.referred_by?.id;
  return isTrue;
};

// for clinic pov sent referrals
// wherein the affiliated dentist's id is the referred_by
// and the referred_by_clinic_id is the clinic's id
// or if clinic is the referred to id
const isClinicReferralSent = (user: any, data: any) => {
  const isTrue = user?.type === 'dental-clinic'
    && ((user?.id === data?.created_by && user?.id !== data?.referred_to_clinic_id)
      || (user?.id === data?.referred_by?.id)
      || (user?.affiliated_dentists.indexOf(data?.referred_by?.id) !== -1
        && user?.id === data?.referred_by_clinic_id
        && user?.id !== data?.referred_to?.id
        && user?.id !== data?.referred_to_clinic_id));
  return isTrue;
};

const isClinicCreatedReferralAndReferredToItsDentist = (user: any, data: any) => {
  const isTrue = user?.type === 'dental-clinic'
    && ((user?.id === data?.created_by && data?.referred_to_is_my_dentist));
  return isTrue;
};

// for clinic pov received referrals
const isClinicReferralReceived = (user: any, data: any) => {
  const isTrue = user?.type === 'dental-clinic'
    // not created by clinic and referred to the clinic
    && ((user?.id !== data?.created_by && user?.id === data?.referred_to?.id)
      // referred_to the affiliated dentist and referred_to_clinic user
      || ((user?.affiliated_dentists.indexOf(data?.referred_to?.id) !== -1
        && user?.id === data?.referred_to_clinic_id
        // and should not be referred_by to the affiliated dentist and
        // not referred_by_clinic user
        && user?.affiliated_dentists.indexOf(data?.referred_by?.id) === -1
        && user?.id !== data?.referred_by_clinic_id))
      // or referred to the affiliated dentist and clinic but not referred_by_clinic_id
      || ((user?.affiliated_dentists.indexOf(data?.referred_to?.id) !== -1
        && user?.id === data?.referred_to_clinic_id
        // and should not be referred_by_clinic user
        && user?.id !== data?.referred_by_clinic_id && user?.id !== data?.referred_by?.id)));
  return isTrue;
};

// this function is used for referrals that are both
// referred_by and referred_to clinic names are the same
// it means both dentists are affiliated with the clinic
const isReferralReferredToReferredByClinicName = (user: any, data: any) => {
  const isTrue = user?.type === 'dental-clinic'
    && (user?.affiliated_dentists.indexOf(data?.referred_to?.id) !== -1
      && user?.id === data?.referred_to_clinic_id)
    && (user?.affiliated_dentists.indexOf(data?.referred_by?.id) !== -1
      && user?.id === data?.referred_by_clinic_id);
  return isTrue;
};

// this function is used for displaying of internal referral icons
const isInternalReferral = (user: any, data: any) => {
  const isTrue = user?.type === 'dental-clinic'
    && (data?.referred_by_clinic_id && user?.id === data?.referred_by_clinic_id)
    && (data?.referred_to_clinic_id && user?.id === data?.referred_to_clinic_id);
  return isTrue;
};

const isReferralSentSystemMessage = (
  msg?: { message?: string; message_type?: string }) => !!msg?.message
  && msg.message_type === 'system'
  && msg.message.startsWith('Referral sent to');

const isNewMessage = (u?: User, d?: Data) => d?.message?.id
  && !d?.message?.reads?.id;
  // && u?.id !== d.message?.sender_id
  // && !isReferralSentSystemMessage(d.message);

// used for messages column wherein the new badge is displayed
// for the received referrals
// if the status is pending and the user is the referred_to id
// or if clinic pov, the referred_by and referred_by_clinic_id is not the clinic's id
// and the referred_to_clinic_id is the clinic's id
const newBadgeIconReceived = (user: any, data: any) => {
  const isPending = data?.status === 'pending'
    && (user?.id === data.referred_to?.id
      || (user?.id !== data?.referred_by?.id && user?.id === data?.referred_to_clinic_id))
    && !data?.message?.id;

  // Only show badge if "Referral sent to" AND not read
  const isReferralSent = data?.status === 'pending'
    && (user?.id === data.referred_to?.id
      || (user?.id !== data?.referred_by?.id && user?.id === data?.referred_to_clinic_id))
    && isReferralSentSystemMessage(data?.message)
    // && user?.id !== data?.message?.sender_id
    && !data?.message?.reads?.id;

  return isPending || isReferralSent;
};

const stripHtmlTags = (html: string): string => {
  if (isEmpty(html) || typeof html !== 'string') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const stripNameDesignation = (name: string) => {
  if (!name) return '';
  return name?.replace(/^Dr\. /i, '');
};

const wait = (time: number) => new Promise(resolve => {
  setTimeout(resolve, time);
});
const getFileExtension = (filename: string) => (filename ? filename.split('.').pop()?.toLowerCase() || '' : '');

const getLatestFollowUp = (followUps: any) => {
  if (!followUps?.length) return null;
  return followUps.reduce((latest: any, current: any) => (
    new Date(current.created_at) > new Date(latest.created_at)
      ? current
      : latest),
  );
};

const viewableAttachmentType = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
];
const imageFileExtensions = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'tiff',
  'tif',
  'svg',
  'ico',
  'heic',
  'avif',
];

const getFileName = (filePath: any) => {
  if (!filePath) return '';
  const pathArray = filePath.split('/');
  return pathArray[pathArray.length - 1];
};

export const STANDARD_DATE_FORMAT = 'MM-DD-YYYY';
export const STANDARD_DATE_TIME_FORMAT = 'MM-DD-YYYY HH:mm:ss';

const formatDate = (
  date: moment.MomentInput,
  format: string = STANDARD_DATE_FORMAT,
) => {
  if (!date) return '';
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(format) : '';
};

const formatDateTime = (
  date: moment.MomentInput,
  format: string = STANDARD_DATE_TIME_FORMAT,
) => {
  if (!date) return '';
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(format) : '';
};

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const tupleMatchesValue = (tuple: [string, string], value: string) => `${tuple[0]}|${tuple[1]}` === value;

const cleanText = (value: string) => value.replace(/[\u200B-\u200D\uFEFF]/g, '');

const removeHtmlTags = (value?: string): string => {
  if (!value || value === '') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');
  return doc.body.textContent || '';
};

// use this to sanitize the filenames to avoid issues when extracting the files
const sanitizeFilename = (name: string) => name.replace(/[<>:"/\\|?*]/g, '_');

export {
  currencyFormat,
  shortDateFormat,
  humanize,
  dateFormat,
  time24HourFormat,
  time12HourFormat,
  timestampToMonthDay,
  timestampToDate,
  timestampToDayOfWeek,
  timestampToDateTime,
  normalizeMobile,
  serverDateFormat,
  getCurrentVersion,
  isLatestVersion,
  setLatestVersion,
  formatEmail,
  getFormData,
  mobileAndTabletCheck,
  formatShortNumber,
  stringEscape,
  getInitials,
  formatBytes,
  calculateAgeInDays,
  localDate,
  formatPhoneNumber,
  formatDecimal,
  formatFieldName,
  trimWysiwygValue,
  isDentistReferralSent,
  isDentistReferralReceived,
  isClinicReferralSent,
  isClinicReferralReceived,
  isReferralReferredToReferredByClinicName,
  isInternalReferral,
  newBadgeIconReceived,
  stripHtmlTags,
  stripNameDesignation,
  wait,
  isClinicCreatedReferralAndReferredToItsDentist,
  getFileExtension,
  viewableAttachmentType,
  getFileName,
  imageFileExtensions,
  formatDate,
  formatDateTime,
  isNewMessage,
  getLatestFollowUp,
  tupleMatchesValue,
  formatDuration,
  cleanText,
  removeHtmlTags,
  sanitizeFilename,
};
